import { SubscriberRelationshipEnum } from 'domains/client';
import { PayerTypes } from 'domains/signup/data/types';
import { CommonFieldsSchema, getAgeValidation, RequiredErrorMessage } from 'domains/signupDirect/schemas/common';
import { PaymentMethod } from 'domains/signupDirect/schemas/filtersSchema';
import { EligibilityCheckResult } from 'domains/signupv4/Insurance/types';
import { isUndefined } from 'lodash';
import { TypeInsuranceFields } from 'types/contentful';
import { z } from 'zod';
import { EvaluationScheduleSchema } from './evaluationScheduleSchema';

const PPInfo = z.object({
  name: z.string(RequiredErrorMessage).min(1, RequiredErrorMessage),
  officeName: z.string(RequiredErrorMessage).min(1, RequiredErrorMessage),
  phone: z.string().optional(),
  website: z.string().optional(),
});

const InsuranceInfo = z.object({
  insuranceData: PaymentMethod.options[0].pick({
    insuranceData: true,
  }).shape.insuranceData,
  memberId: z.string(RequiredErrorMessage).min(1, RequiredErrorMessage),
  groupId: z.string(RequiredErrorMessage).optional(),
  eligibilityCheckResult: z.nativeEnum(EligibilityCheckResult).optional(),
  preApprovedEligibilityCheck: z.boolean().optional(),
});

const PrimaryInsuranceSubscriber = z
  .object({
    relationShipToClient: z.nativeEnum(SubscriberRelationshipEnum, RequiredErrorMessage),
    dateOfBirth: getAgeValidation(true),
    firstName: z.string(RequiredErrorMessage).optional(),
    lastName: z.string(RequiredErrorMessage).optional(),
  })
  .refine(
    data => {
      const isSelf = data.relationShipToClient === SubscriberRelationshipEnum.Self;
      const isValidDOB = !isUndefined(data.dateOfBirth);
      return isSelf ? true : isValidDOB;
    },
    {
      message: RequiredErrorMessage.message,
      path: ['dateOfBirth'],
    },
  )
  .refine(data => (data.relationShipToClient !== SubscriberRelationshipEnum.Self ? data.firstName : true), {
    message: RequiredErrorMessage.message,
    path: ['firstName'],
  })
  .refine(data => (data.relationShipToClient !== SubscriberRelationshipEnum.Self ? data.lastName : true), {
    message: RequiredErrorMessage.message,
    path: ['lastName'],
  });

// Shared CC info
const CCPaymentInfo = z.object({
  stripeMethodId: z.string(RequiredErrorMessage).min(1, RequiredErrorMessage),
});

// Medicaid
export const MedicaidBilling = InsuranceInfo.extend({
  type: z.literal('insurance-medicaid'),
  primaryPhysician: PPInfo,
});

// Commercial Insurance
export const CommercialInsuranceBilling = InsuranceInfo.extend({
  type: z.literal('insurance-commercial'),
  stripeMethodId: CCPaymentInfo.shape.stripeMethodId.optional(),
  subscriber: PrimaryInsuranceSubscriber,
});

// Medicare
export const MedicareBilling = InsuranceInfo.extend({
  type: z.literal('insurance-medicare'),
  primaryPhysician: PPInfo,
});

// Private Pay billing
export const PrivatePayBilling = CCPaymentInfo.extend({
  type: z.literal('privatePay'),
});

export const BillingInfo = z
  .discriminatedUnion('type', [PrivatePayBilling, MedicaidBilling, MedicareBilling, CommercialInsuranceBilling])
  .refine(
    data => {
      // Note(juan): this needs to be done here sadly, after the discriminated union merge
      if (data.type === 'insurance-commercial') {
        if (data.eligibilityCheckResult === EligibilityCheckResult.IN_NETWORK || data.preApprovedEligibilityCheck) {
          return data.stripeMethodId;
        }

        if (data.eligibilityCheckResult === EligibilityCheckResult.OUT_OF_NETWORK) {
          return data.stripeMethodId;
        }
        return true;
      } else {
        return true;
      }
    },
    {
      message: RequiredErrorMessage.message,
      path: ['stripeMethodId'],
    },
  );

// Form schemas
export const EvaluationForm = CommonFieldsSchema.extend({
  evaluationSchedule: EvaluationScheduleSchema,
  billingInfo: BillingInfo,
  leadId: z.string().optional(),
  insuranceLeadId: z.string().optional(),
  consentToTerms: z.boolean().optional(),
});

export type EvaluationFormType = z.infer<typeof EvaluationForm>;

export const paymentMethodIsInsurance = (paymentData: EvaluationFormType['billingInfo']) => {
  return (
    paymentData.type === 'insurance-commercial' ||
    paymentData.type === 'insurance-medicaid' ||
    paymentData.type === 'insurance-medicare'
  );
};

export const paymentTypeIsInsurance = (type: EvaluationFormType['billingInfo']['type']) => {
  return type === 'insurance-commercial' || type === 'insurance-medicaid' || type === 'insurance-medicare';
};

export const getInsuranceSchemaType = (insuranceType: TypeInsuranceFields['insuranceType']) => {
  switch (insuranceType) {
    case 'Commercial':
      return 'insurance-commercial';
    case 'Medicaid':
      return 'insurance-medicaid';
    case 'Medicare':
      return 'insurance-medicare';
  }
};

export const getPayerTypeFromBillingInfo = (info: EvaluationFormType['billingInfo']): PayerTypes => {
  switch (info.type) {
    case 'insurance-commercial':
      return PayerTypes.COMMERCIAL;
    case 'insurance-medicaid':
      return PayerTypes.MEDICAID;
    case 'insurance-medicare':
      return PayerTypes.MEDICARE;
    case 'privatePay':
      throw `Couldn't map billing info to payer type, info type was ${info.type}`;
  }
};
